/**
 * Central Amplitude client for the GG web app.
 * All logEvent calls should go through logEvent() so property keys and values
 * stay consistent (legacy + canonical keys during transition).
 */
import amplitude from 'amplitude-js';
import config from './config';

let initialized = false;

/** Legacy display keys → canonical snake_case keys (both are sent during transition). */
const LEGACY_TO_CANONICAL = {
  'gift amount': 'gift_amount',
  'first fund': 'first_fund',
  'gift status': 'gift_status',
  'gift type': 'gift_type',
  'method type': 'method_type',
  'chosen amount': 'chosen_amount',
  'last page': 'last_page',
  'signup-popup': 'signup_popup',
  'user-gifting': 'user_gifting',
};

const CANONICAL_TO_LEGACY = Object.fromEntries(
  Object.entries(LEGACY_TO_CANONICAL).map(([legacy, canonical]) => [canonical, legacy])
);

function legacyValueForCanonical(canonicalKey, value) {
  if (canonicalKey === 'gift_status' && value != null) {
    return String(value).toLowerCase();
  }
  if (canonicalKey === 'user_gifting') {
    return value ? 'true' : 'false';
  }
  if (canonicalKey === 'signup_popup') {
    return value ? 'Open' : 'Close';
  }
  return value;
}

/** Normalize and mirror legacy ↔ canonical property keys on every event payload. */
export function expandEventProperties(properties = {}) {
  const out = { ...properties };

  for (const [key, value] of Object.entries(properties)) {
    if (value === undefined) continue;

    const canonical = LEGACY_TO_CANONICAL[key];
    if (canonical) {
      out[canonical] = legacyValueForCanonical(canonical, value);
    }

    const legacy = CANONICAL_TO_LEGACY[key];
    if (legacy) {
      out[legacy] = legacyValueForCanonical(key, value);
    }
  }

  if (out.gift_status != null) {
    out.gift_status = String(out.gift_status).toLowerCase();
    out['gift status'] = out.gift_status;
  } else if (out['gift status'] != null) {
    out['gift status'] = String(out['gift status']).toLowerCase();
    out.gift_status = out['gift status'];
  }

  return out;
}

/** Properties for `gifting: start gift` — booleans + legacy string mirrors. */
export function giftingStartGiftProperties({ source, signupPopupOpen, userGifting }) {
  return expandEventProperties({
    source,
    signup_popup: !!signupPopupOpen,
    'signup-popup': signupPopupOpen ? 'Open' : 'Close',
    user_gifting: !!userGifting,
    'user-gifting': userGifting ? 'true' : 'false',
  });
}

export function initAnalytics() {
  if (!initialized) {
    amplitude.getInstance().init(config.amp_key);
    initialized = true;
  }
}

export function logEvent(eventName, properties = {}) {
  initAnalytics();
  amplitude.getInstance().logEvent(eventName, expandEventProperties(properties));
}

export function setAnalyticsUserId(userId) {
  initAnalytics();
  if (userId == null || userId === '') return;
  amplitude.getInstance().setUserId(String(userId));
}

export function clearAnalyticsUserId() {
  initAnalytics();
  amplitude.getInstance().setUserId(null);
}
